import React, { forwardRef, useEffect, useState } from "react";
import { StepRef, StepProps } from "../types";
import { Typography, Box, Button, TextField, Alert } from "@mui/material";
import useIsMobile from "components/hooks/useIsMobile";
import { celebrate } from "../../../common/celebrations";
import StaticStep from "../components/StaticStep";
import PasswordInput from "components/common/PasswordInput";
import { validatePassword } from "common/utils";
import AuthService from "services/auth";
import PhoneInput from "components/creation/PhoneInput";
import { cleanPhone } from "common/utils";
import { checkPhone } from "common/userUtils";

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
  password: string;
}

interface FormErrors {
  [key: string]: string;
}

export default forwardRef<StepRef, StepProps>(
  ({ onNext, disabled, signupState }, _ref) => {
    const isMobile = useIsMobile();
    const [formData, setFormData] = useState<FormData>({
      firstName: signupState?.userInfo?.firstName || "",
      lastName: signupState?.userInfo?.lastName || "",
      phone: signupState?.userInfo?.phone || "",
      password: "",
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [previousPhone, setPreviousPhone] = useState<string>("");

    const { request: createPasswordRequest, loading: createPasswordLoading } =
      AuthService.useCreatePassword();

    useEffect(() => {
      setTimeout(() => {
        celebrate();
      }, 500);
    }, []);

    const validateForm = (): FormErrors => {
      const newErrors: FormErrors = {};

      // First name validation
      if (!formData.firstName.trim()) {
        newErrors.firstName = "First name is required";
      }

      // Last name validation
      if (!formData.lastName.trim()) {
        newErrors.lastName = "Last name is required";
      }

      // Phone validation
      if (!formData.phone) {
        newErrors.phone = "Phone is required";
      } else if (!checkPhone(formData.phone)) {
        newErrors.phone = "Invalid phone number";
      }

      // Password validation
      const passwordErrors = validatePassword(formData.password);
      Object.assign(newErrors, passwordErrors);

      return newErrors;
    };

    const handleContinue = async () => {
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      // Create user account
      try {
        // Update signup state with user info
        const response = await onNext?.({
          userInfo: {
            email: signupState?.userInfo?.email || "",
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone,
            address: signupState?.userInfo?.address || "",
          },
        });
        // If we got a token back, trade it for authentication tokens
        if (response?.token) {
          await AuthService.signupTokenLogin(response.token);
          await createPasswordRequest({ password: formData.password });
        }
      } catch (error) {
        setErrors({ general: "Failed to create account. Please try again." });
      }
    };

    const createChangeHandler = (field: keyof FormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

    return (
      <StaticStep
        title="Welcome to Faye! 🎉"
        img="/assets/images/nux/signup-confirmation.png"
        mobileImg="/assets/images/nux/signup-confirmation.png"
        fullImageHeight={true}
        bodyContent={
          <>
            <Typography
              variant="h4"
              sx={{ marginBottom: "12px", fontWeight: "500" }}
            >
              Let's finish setting up your account.
            </Typography>

            {errors.general && (
              <Alert severity="error" sx={{ marginBottom: "16px" }}>
                {errors.general}
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: "16px" }}>
              <TextField
                sx={{ flex: 1 }}
                label="First name"
                type="text"
                variant="outlined"
                name="firstName"
                autoComplete="given-name"
                value={formData.firstName}
                error={!!errors.firstName}
                helperText={errors.firstName}
                onChange={(e) =>
                  createChangeHandler("firstName", e.target.value)
                }
              />
              <TextField
                sx={{ flex: 1 }}
                label="Last name"
                type="text"
                variant="outlined"
                name="lastName"
                autoComplete="family-name"
                value={formData.lastName}
                error={!!errors.lastName}
                helperText={errors.lastName}
                onChange={(e) =>
                  createChangeHandler("lastName", e.target.value)
                }
              />
            </Box>

            <Box>
              <PhoneInput
                value={formData.phone}
                error={!!errors.phone}
                helperText={errors.phone}
                onChange={(phone) => {
                  let newPhone = cleanPhone(
                    typeof phone == "string" ? phone : phone.target.value,
                  );
                  // Handle auto complete where the 1 country code is not included
                  if (
                    (!previousPhone || previousPhone.length === 1) &&
                    newPhone.length > 10 &&
                    !newPhone.startsWith("+1")
                  ) {
                    newPhone = `+1${newPhone.replace("+", "")}`;
                  }
                  createChangeHandler("phone", newPhone);
                  setPreviousPhone(newPhone);
                }}
                label="Phone"
                name="phone"
                size="small"
                sx={{ width: "100%" }}
                autoComplete="tel"
              />
            </Box>

            <Box sx={{ marginBottom: "24px" }}>
              <PasswordInput
                password={formData.password}
                onChange={(password) =>
                  createChangeHandler("password", password)
                }
                sx={{ width: "100%" }}
              />
            </Box>
          </>
        }
        actions={
          <>
            <Button
              key={0}
              variant="contained"
              color="primary"
              size={isMobile ? "medium" : "large"}
              disabled={disabled || createPasswordLoading}
              onClick={handleContinue}
            >
              Continue
            </Button>
          </>
        }
      />
    );
  },
);
